/**
 * Custom routes with special guards in the render function
 */
import React, { useContext, useEffect } from 'react';
import { Route, Redirect } from 'react-router-dom';
import AppContext from '../../contexts/AppContext';
import AuthenticationContext from '../../contexts/AuthenticationContext';
import RolesContext from '../../contexts/RolesContext';
import Loading from '../Shared/ResourceIndex/Loading';
import { env } from '../../config';
import { useOffline } from '../../contexts/OfflineContext';
import { FormContextProvider } from '@stormwater-software/lib-form';
import PublicInfoModal from '../PublicUrl/Component/PublicInfoModal';
import projectApi from '../../utils/api/project';

/**
 * Private route. Only accessible by logged-in user.
 */
export const PrivateRoute = (routeProps) => {
  const { Component, Layout, checking, ...rest } = routeProps;
  const appStore = useContext(AppContext);
  const authStore = useContext(AuthenticationContext);
  const rolesStore = useContext(RolesContext);
  const user = appStore.get('user');

  if (!appStore.initialDataLoaded) {
    return <Loading />;
  }

  return (
    <Route
      exact
      {...rest}
      render={(props) =>
        authStore.loggedIn &&
        rolesStore.navigationIsAllowed(props.location.pathname) ? (
          <Layout user={user} checking={checking} {...props}>
            <Component user={user} {...props} />
          </Layout>
        ) : (
          <Redirect
            to={{
              pathname: '/login',
              search: `?to=${props.location.pathname}${props.location.search}`,
              state: { from: props.location.pathname },
            }}
          />
        )
      }
    />
  );
};

export const PublicUrlRoute = (routeProps) => {
  const { Component, Layout, ...rest } = routeProps;
  const authStore = useContext(AuthenticationContext);
  const [showPublicInfoModal, setShowPublicInfoModal] = React.useState(false);
  const [statusOfPublicProject, setStatusOfPublicProject] = React.useState('loading');
  const [visitorGateEnabled, setVisitorGateEnabled] = React.useState(false);
  const [visitorGateRequired, setVisitorGateRequired] = React.useState(true);
  const [publicProjectName, setPublicProjectName] = React.useState(null);
  const [publicClientName, setPublicClientName] = React.useState(null);
  // Token is now in HTTP-only secure cookie, no need to check sessionStorage
  const hasImage = routeProps.location.search.startsWith('?image');
  const projectId = routeProps.location.pathname.includes('projects/')
    ? routeProps.location.pathname.split('projects/')[1]?.split('/')[0]
    : null;
  const inspectionId = routeProps.location.pathname.includes('inspection/')
    ? routeProps.location.pathname.split('inspection/')[1]?.split('/')[0]
    : null;
  // Gate is a client-level setting, so completion is tracked per client for the
  // whole session. The key is derived from the API response (set in the effect
  // below) rather than from the URL's resource id — otherwise navigating from a
  // project to its inspections/findings (whose URLs carry no projectId) would
  // flip to an unset key and re-trigger the gate on every card.
  const [gateSessionKey, setGateSessionKey] = React.useState(null);
  const gateCompletedForThis = gateSessionKey
    ? sessionStorage.getItem(gateSessionKey) === '1'
    : false;
  // Pre-populate gate form from a previous submission in this session
  const previousVisitorData = React.useMemo(() => {
    try {
      const stored = sessionStorage.getItem('pvVisitorData');

      
return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  }, []);

  /**
   * SW2-2143: an old QR/public link can encode a stale tenant subdomain (e.g. app.sw2.net)
   * for a project that has since moved to another tenant (e.g. dhi.sw2.net). The backend
   * `isPublicAvailable` response carries the project's CURRENT `tenantDomain` (resolved via
   * project -> client -> tenant) and, for project links, the current `divisionId`. If the
   * current subdomain doesn't match, redirect to the correct tenant domain, correcting the
   * (possibly stale) `/divisions/:id` segment and preserving the query string.
   * Returns true if a redirect was triggered (caller should stop — the page is navigating away).
   */
  const redirectToCorrectTenantIfNeeded = (response) => {
    const { host, protocol, pathname, search } = window.location;
    const labels = host.split('.');
    // No subdomain to compare against (e.g. localhost / bare domain) — skip.
    if (labels.length <= 1) return false;
    const currentSubdomain = labels[0];
    const targetDomain = response && response.tenantDomain;
    if (!targetDomain || targetDomain === currentSubdomain) return false;
    const rootHost = labels.slice(1).join('.');
    const correctedPath = response.divisionId
      ? pathname.replace(/(\/divisions\/)\d+(\/|$)/, `$1${response.divisionId}$2`)
      : pathname;
    window.location.href = `${protocol}//${targetDomain}.${rootHost}${correctedPath}${search}`;
    return true;
  };

  useEffect(async () => {
    if (projectId || inspectionId) {
      if (projectId) {
        const response = await projectApi.isPublicAvailable(
          projectId,
          'project',
        );
        if (!response.isPublicAvailable) {
          setStatusOfPublicProject('unavailable');
          return;
        }
        // Redirect to the project's current tenant domain if this link is on the wrong subdomain.
        if (redirectToCorrectTenantIfNeeded(response)) return;
        setVisitorGateEnabled(response.visitorGateEnabled ?? false);
        setVisitorGateRequired(response.visitorGateRequired ?? true);
        setPublicProjectName(response.projectName ?? null);
        setPublicClientName(response.clientName ?? null);
        setGateSessionKey(
          `pvGate_client_${response.clientName ?? response.projectName ?? 'public'}`,
        );
      } else if (inspectionId) {
        const response = await projectApi.isPublicAvailable(
          inspectionId,
          'inspection',
        );
        if (!response.isPublicAvailable) {
          setStatusOfPublicProject('unavailable');
          return;
        }
        if (redirectToCorrectTenantIfNeeded(response)) return;
        setVisitorGateEnabled(response.visitorGateEnabled ?? false);
        setVisitorGateRequired(response.visitorGateRequired ?? true);
        setPublicProjectName(response.projectName ?? null);
        setPublicClientName(response.clientName ?? null);
        setGateSessionKey(
          `pvGate_client_${response.clientName ?? response.projectName ?? 'public'}`,
        );
      }
      setStatusOfPublicProject('available');
    } else {
      setStatusOfPublicProject('loading');
    }
  }, [projectId, inspectionId]);

  const getTenant = async (name) => {
    try {
      const tenantData = await authStore.getTenantByName(name);

      if (!tenantData) {
        window.location.href = `${process.env.REACT_APP_LOGIN_URL}/login`;
      }

      return tenantData;
    } catch (error) {
      window.location.href = `${process.env.REACT_APP_LOGIN_URL}/login`;
    }
  };

  const performLogin = async (publicUserData) => {
    const { host, origin, pathname } = window.location;

    // Only run for subdomains & non-login origin
    if (
      host.split('.').length <= 1 ||
      origin === process.env.REACT_APP_LOGIN_URL
    ) {
      return;
    }

    const tenantName = host.split('.')[0];
    const username = hasImage
      ? env.REACT_APP_USERNAME_NO_IMAGE
      : env.REACT_APP_USERNAME;

    try {
      const tenantData = await getTenant(tenantName);
      const projectId = pathname.includes('projects/')
        ? pathname.split('projects/')[1]?.split('/')[0]
        : null;

      if (tenantData) {
        await authStore.publiclogin(
          username,
          env.REACT_APP_PASSWORD,
          tenantData.domain,
          {
            publicUserName: publicUserData.name,
            publicUserEmail: publicUserData.email,
            publicUserCompany: publicUserData.company,
            publicLink: `${origin}${pathname}`,
            projectId,
          },
        );
      } else {
        if (!projectId) {
          window.location.href = `${process.env.REACT_APP_LOGIN_URL}/login`;
          return;
        }
        await authStore.publicLoginWithProject(
          username,
          env.REACT_APP_PASSWORD,
          projectId,
          {
            publicUserName: publicUserData.name,
            publicUserEmail: publicUserData.email,
            userCompany: publicUserData.company,
            publicLink: `${origin}${pathname}`,
            projectId,
          },
        );
      }

      setShowPublicInfoModal(false);
      if (gateSessionKey) sessionStorage.setItem(gateSessionKey, '1');
      window.location.reload();
    } catch (err) {
      console.error('Public login failed', err);
    }
  };

  useEffect(async () => {
    // Authenticated SW2 users who opened public view from the app (Qr Access key)
    // are pre-approved — never show the gate regardless of client settings.
    const isAppUser = localStorage.getItem('Qr Access') === 'true';

    if (isAppUser || gateCompletedForThis) {
      setShowPublicInfoModal(false);
      // Still need to establish the public session if not already logged in
      if (!authStore.loggedIn && statusOfPublicProject === 'available') {
        performLogin({ name: '', email: '', company: '' });
      }

      return;
    }

    // Only show the gate once we know the gate settings (statusOfPublicProject is available)
    if (statusOfPublicProject === 'available') {
      if (visitorGateEnabled) {
        setShowPublicInfoModal(true);
      } else {
        // Gate disabled — auto-proceed as anonymous visitor
        performLogin({ name: '', email: '', company: '' });
      }
    }
  }, [authStore.loggedIn, gateCompletedForThis, statusOfPublicProject, visitorGateEnabled]);

  const handlePublicInfoSubmit = (data) => {
    sessionStorage.setItem('pvVisitorData', JSON.stringify({
      name: data.name || '',
      email: data.email || '',
      company: data.company || '',
    }));
    performLogin(data);
  };

  const handlePublicInfoSkip = () => {
    performLogin({ name: '', email: '', company: '' });
  };

  const handlePublicInfoCancel = () => {
    window.location.href = `${process.env.REACT_APP_LOGIN_URL}/login`;
  };

  if (statusOfPublicProject === 'loading') {
    return <Loading />;
  }
  if (statusOfPublicProject === 'unavailable') {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>This project is not available</h2>
        <p>
          The project you are trying to access is currently not
          available publicly. Please contact the project owner for more information.
        </p>
      </div>
    );
  }
  return (
    <>
      {showPublicInfoModal ? (
        <FormContextProvider initialValue={previousVisitorData}>
          <PublicInfoModal
            onSubmit={handlePublicInfoSubmit}
            onCancel={handlePublicInfoCancel}
            onSkip={handlePublicInfoSkip}
            required={visitorGateRequired}
            projectName={publicProjectName}
            clientName={publicClientName}
          />
        </FormContextProvider>
      ) : (
        <Route
          exact
          {...rest}
          render={(props) => (
            <Layout {...props}>
              <Component {...props} />
            </Layout>
          )}
        />
      )}
    </>
  );
};

/**
 * Public route. Only acessible by annon/guest user
 */
export const PublicRoute = (routeProps) => {
  const { Component, Layout, ...rest } = routeProps;
  const authStore = useContext(AuthenticationContext);

  const isOffline = useOffline();

  /** When logged in, force logout, before rendering */
  useEffect(() => {
    if (authStore.loggedIn && !isOffline) {
      authStore.logout();
    }
  }, []);

  return (
    <Route
      exact
      {...rest}
      render={(props) => (
        <Layout {...props}>
          <Component {...props} />
        </Layout>
      )}
    />
  );
};

/**
 * Generic route. Accesible by anyone.
 */
export const GenericRoute = (routeProps) => {
  const { Component, Layout, ...rest } = routeProps;

  return (
    <Route
      exact
      {...rest}
      render={(props) => (
        <Layout {...props}>
          <Component {...props} />
        </Layout>
      )}
    />
  );
};

export const GuestUrlRoute = (routeProps) => {
  const { Component, Layout, ...rest } = routeProps;
  const authStore = useContext(AuthenticationContext);
  // Token is now in HTTP-only secure cookie, no need to check sessionStorage

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(async () => {
    if (
      window.location.host.split('.').length > 1 &&
      window.location.origin !== process.env.REACT_APP_LOGIN_URL
    ) {
      const tenantName = window.location.host.split('.')[0];

      const tenantData = await getTenant(tenantName);

      // Always attempt login since token is in HTTP-only cookie
      if (tenantData) {
        authStore
          .guestlogin(
            env.REACT_APP_GUEST_USER_EMAIL,
            env.REACT_APP_GUEST_USER_PASSWORD,
            tenantData.domain,
          )
          .then((res) => {
            console.log('Guest login success');
          });
      }
    }
  }, [window.location.host]);

  const getTenant = async (name) => {
    try {
      const tenantData = await authStore.getTenantByName(name);

      if (!tenantData) {
        window.location.href = `${process.env.REACT_APP_LOGIN_URL}/login`;
      }

      return tenantData;
    } catch (error) {
      window.location.href = `${process.env.REACT_APP_LOGIN_URL}/login`;
    }
  };

  return (
    <Route
      exact
      {...rest}
      render={(props) => (
        <Layout {...props}>
          <Component {...props} />
        </Layout>
      )}
    />
  );
};

export { DivisionRoute } from './DivisionRoute';
export { RedirectRoute } from './RedirectRoute';
